import { ChevronLeft, ChevronRight } from "lucide-react";
import * as React from "react";
import { Button } from "@/components/ui/button";
import { Label } from "@/components/ui/label";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";

interface TablePaginationProps {
  page: number;
  rowsPerPage: number;
  rows: number;
  onPageChange: (newPage: number) => void;
  onRowsPerPageChange: (newValue: number) => void;
  rowsPerPageOptions?: number[];
}

const defaultOptions = [10, 50, 100, 500, 1000];

const TablePagination = ({
  page,
  rowsPerPage,
  rows,
  onPageChange,
  onRowsPerPageChange,
  rowsPerPageOptions = defaultOptions,
}: TablePaginationProps) => {
  const getLabelDisplayedRowsTo = () => {
    if (rows === -1)
      return (page + 1) * rowsPerPage;

    return rowsPerPage === -1
      ? rows
      : Math.min(rows, (page + 1) * rowsPerPage);
  };

  function labelDisplayedRows({from, to, count,}: {
    from: number;
    to: number;
    count: number;
  }) {
    return `${from}\u2013${to} of ${count !== -1 ? count : `more than ${to}`}`;
  }

  return (
    <div className="bg-muted/50 border-t shrink-0 flex items-center justify-end gap-4 px-4 py-2">
      <div className="flex items-center gap-2">
        <Label className="text-sm whitespace-nowrap">Rows per page:</Label>
        <Select
          value={String(rowsPerPage)}
          onValueChange={(val) => onRowsPerPageChange(Number(val))}
        >
          <SelectTrigger size="sm">
            <SelectValue />
          </SelectTrigger>
          <SelectContent>
            {rowsPerPageOptions.map((opt) => (
              <SelectItem key={opt} value={String(opt)}>
                {opt}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
      </div>
      <span className="min-w-[80px] text-center text-sm">
        {labelDisplayedRows({
          from: rows === 0 ? 0 : page * rowsPerPage + 1,
          to: getLabelDisplayedRowsTo(),
          count: rows === -1 ? -1 : rows,
        })}
      </span>
      <div className="flex gap-1">
        <Button
          variant="outline"
          size="icon-sm"
          disabled={page === 0}
          onClick={() => onPageChange(page - 1)}
        >
          <ChevronLeft className="size-4" />
        </Button>
        <Button
          variant="outline"
          size="icon-sm"
          disabled={
            rows !== -1
              ? page >= Math.ceil(rows / rowsPerPage) - 1
              : false
          }
          onClick={() => onPageChange(page + 1)}
        >
          <ChevronRight className="size-4" />
        </Button>
      </div>
    </div>
  );
};

export default TablePagination;
